import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import type { Comment, CreateCommentDTO } from '../types/comment.types'

import { httpClient } from '@/shared/utils/http-client'

const API_BASE_URL = import.meta.env.DEV
  ? '/user/v1/admin'
  : 'https://scout-service-v2.enter.de/user/v1/admin'

export const useCommentsQuery = (objectUuid: string) => {
  return useQuery<Comment[]>({
    queryKey: ['comments', objectUuid],
    queryFn: async (): Promise<Comment[]> => {
      const response = await httpClient.fetch(`${API_BASE_URL}/comment/${objectUuid}`)

      if (!response.ok) {
        if (response.status === 404) {
          return []
        }
        throw new Error('Failed to fetch comments')
      }

      const data = (await response.json()) as Comment[]
      return data || []
    },
    enabled: !!objectUuid,
  })
}

export const useCreateCommentMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: CreateCommentDTO) => {
      const response = await httpClient.fetch(`${API_BASE_URL}/comment`, {
        method: 'POST',
        body: JSON.stringify(data),
      })

      if (!response.ok) {
        if (response.status === 400) {
          const errorData = await response.json()
          throw new Error((errorData.error as string) || 'Validation failed')
        }
        throw new Error('Failed to create comment')
      }

      const result = await response.json()
      return result as Comment
    },
    onSuccess: (_, data) => {
      // Invalidate comments for this object
      queryClient.invalidateQueries({ queryKey: ['comments', data.objectUuid] })
    },
  })
}
